import React, { useMemo, useState } from "react";
import { StatCard } from "../components/StatCard";
import { Button } from "../components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { FileText, Download, Calendar, TrendingUp, TrendingDown, DollarSign, Activity, Check, AlertCircle, X } from "lucide-react";
import { Transaction } from "../types";
import { DataService } from "../services/supabase";
import * as XLSX from 'xlsx';

interface Expense {
  id: string;
  date: string;
  category: string;
  description?: string;
  amount: number;
  created_at: string;
  updated_at: string;
}

interface TaxCenterProps {
  dataService?: DataService | null;
  onDataUpdate?: () => void;
  transactions?: Transaction[];
  expenses?: Expense[];
}

const TaxCenter = ({ dataService, onDataUpdate, transactions = [], expenses = [] }: TaxCenterProps) => {
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [isExporting, setIsExporting] = useState(false);
  const [taxMessage, setTaxMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);
  const showTaxMessage = (type: "success" | "error", text: string) => {
    setTaxMessage({ type, text });
    setTimeout(() => setTaxMessage(null), 4000);
  };
  
  // Get available years from data
  const availableYears = useMemo(() => {
    const years = new Set<number>();
    
    transactions.forEach(tx => {
      if (tx.purchase_date) {
        years.add(new Date(tx.purchase_date).getFullYear());
      }
      if (tx.sale_date) {
        years.add(new Date(tx.sale_date).getFullYear());
      }
    });
    
    expenses.forEach(expense => {
      if (expense.date) {
        years.add(new Date(expense.date).getFullYear());
      }
    });
    
    return Array.from(years).sort((a, b) => b - a);
  }, [transactions, expenses]);
  
  // Filter data for selected year and calculate metrics
  const yearlyData = useMemo(() => {
    const yearStart = new Date(selectedYear, 0, 1);
    const yearEnd = new Date(selectedYear, 11, 31, 23, 59, 59);
    
    const yearTransactions = transactions.filter(tx => {
      const purchaseDate = tx.purchase_date ? new Date(tx.purchase_date) : null;
      const saleDate = tx.sale_date ? new Date(tx.sale_date) : null;
      return (purchaseDate && purchaseDate >= yearStart && purchaseDate <= yearEnd) ||
             (saleDate && saleDate >= yearStart && saleDate <= yearEnd);
    });
    
    const yearExpenses = expenses.filter(expense => {
      const expenseDate = new Date(expense.date);
      return expenseDate >= yearStart && expenseDate <= yearEnd;
    });
    
    let totalRevenue = 0;
    let totalCostBasis = 0;
    let totalFees = 0;
    let totalGradingCosts = 0;
    let totalExpenses = 0;
    let totalSales = 0;
    
    yearTransactions.forEach(tx => {
      const saleDate = tx.sale_date ? new Date(tx.sale_date) : null;
      if (saleDate && saleDate >= yearStart && saleDate <= yearEnd) {
        const salePrice = typeof tx.sale_price === 'number' ? tx.sale_price : parseFloat(tx.sale_price || '0');
        totalRevenue += salePrice;
        totalSales++;
      }
      
      const purchaseDate = tx.purchase_date ? new Date(tx.purchase_date) : null;
      if (purchaseDate && purchaseDate >= yearStart && purchaseDate <= yearEnd) {
        const cost = typeof tx.cost === 'number' ? tx.cost : parseFloat(tx.cost || '0');
        totalCostBasis += cost;
      }
      
      const marketplaceFee = typeof tx.marketplace_fees === 'number' ? tx.marketplace_fees : parseFloat(tx.marketplace_fees || '0');
      const shippingFee = typeof tx.shipping_fees === 'number' ? tx.shipping_fees : parseFloat(tx.shipping_fees || '0');
      totalFees += marketplaceFee + shippingFee;
      
      const gradingCost = typeof tx.grading_cost === 'number' ? tx.grading_cost : parseFloat(tx.grading_cost || '0');
      totalGradingCosts += gradingCost;
    });
    
    yearExpenses.forEach(expense => {
      totalExpenses += expense.amount || 0;
    });
    
    const grossProfit = totalRevenue - totalCostBasis;
    const netProfit = grossProfit - totalFees - totalGradingCosts - totalExpenses;
    
    return {
      yearTransactions,
      yearExpenses,
      totalRevenue,
      totalCostBasis,
      totalFees,
      totalGradingCosts,
      totalExpenses,
      grossProfit,
      netProfit,
      totalSales,
      totalTransactions: yearTransactions.length,
    };
  }, [selectedYear, transactions, expenses]);
  
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);
  };
  
  const handleExportMetrics = async () => {
    setIsExporting(true);
    try {
      const exportData = [
        ['Tax Center Report', `${selectedYear}`],
        ['Generated', new Date().toLocaleDateString()],
        [''],
        ['SUMMARY METRICS'],
        ['Total Revenue', formatCurrency(yearlyData.totalRevenue)],
        ['Total Cost Basis', formatCurrency(yearlyData.totalCostBasis)],
        ['Gross Profit', formatCurrency(yearlyData.grossProfit)],
        ['Total Fees', formatCurrency(yearlyData.totalFees)],
        ['Total Grading Costs', formatCurrency(yearlyData.totalGradingCosts)],
        ['Total Expenses', formatCurrency(yearlyData.totalExpenses)],
        ['Net Profit', formatCurrency(yearlyData.netProfit)],
        [''],
        ['TRANSACTION METRICS'],
        ['Total Transactions', yearlyData.totalTransactions],
        ['Total Sales', yearlyData.totalSales],
      ];
      
      const ws = XLSX.utils.aoa_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Tax Summary');
      
      const transactionData = yearlyData.yearTransactions.map(tx => ({
        'Card': tx.card || '',
        'Purchase Date': tx.purchase_date || '',
        'Cost': typeof tx.cost === 'number' ? tx.cost : parseFloat(tx.cost || '0'),
        'Sale Date': tx.sale_date || '',
        'Sale Price': typeof tx.sale_price === 'number' ? tx.sale_price : parseFloat(tx.sale_price || '0'),
        'Marketplace Fees': typeof tx.marketplace_fees === 'number' ? tx.marketplace_fees : parseFloat(tx.marketplace_fees || '0'),
        'Shipping Fees': typeof tx.shipping_fees === 'number' ? tx.shipping_fees : parseFloat(tx.shipping_fees || '0'),
        'Grading Cost': typeof tx.grading_cost === 'number' ? tx.grading_cost : parseFloat(tx.grading_cost || '0'),
        'Status': tx.status || '',
      }));
      
      const transactionWs = XLSX.utils.json_to_sheet(transactionData);
      XLSX.utils.book_append_sheet(wb, transactionWs, 'Transactions');
      
      const expenseData = yearlyData.yearExpenses.map(expense => ({
        'Date': expense.date || '',
        'Category': expense.category || '',
        'Description': expense.description || '',
        'Amount': expense.amount || 0,
      }));
      
      const expenseWs = XLSX.utils.json_to_sheet(expenseData);
      XLSX.utils.book_append_sheet(wb, expenseWs, 'Expenses');
      
      XLSX.writeFile(wb, `tax-report-${selectedYear}.xlsx`);
    } catch (error) {
      console.error('Export error:', error);
      showTaxMessage('error', 'Failed to export tax report');
    } finally {
      setIsExporting(false);
    }
  };
  
  return (
    <div className="min-h-screen bg-background">
      <main className="container py-8 space-y-8">
        {/* Header */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Tax Center</h1>
            <p className="text-muted-foreground mt-1">
              Year-end tax reporting and summaries
            </p>
          </div>
          <div className="flex gap-2">
            <Select value={String(selectedYear)} onValueChange={(v) => setSelectedYear(Number(v))}>
              <SelectTrigger className="w-[140px]">
                <Calendar className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Select year" />
              </SelectTrigger>
              <SelectContent>
                {availableYears.map(year => (
                  <SelectItem key={year} value={String(year)}>
                    {year}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button 
              variant="gradient" 
              size="sm" 
              className="gap-2"
              onClick={handleExportMetrics}
              disabled={isExporting}
            >
              {isExporting ? (
                <>
                  <span className="loading-dots">Exporting</span>
                </>
              ) : (
                <>
                  <Download className="h-4 w-4" />
                  <span className="hidden sm:inline">Export Report</span>
                  <span className="sm:hidden">Export</span>
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Inline success/error message */}
        {taxMessage && (
          <div className={`flex items-center gap-3 p-4 rounded-lg border ${
            taxMessage.type === "success" ? "bg-success/10 border-success/20" : "bg-destructive/10 border-destructive/20"
          }`}>
            {taxMessage.type === "success" ? (
              <div className="h-8 w-8 rounded-full bg-success/20 flex items-center justify-center flex-shrink-0">
                <Check className="h-5 w-5 text-success" />
              </div>
            ) : (
              <AlertCircle className="h-5 w-5 text-destructive flex-shrink-0" />
            )}
            <p className={`flex-1 text-sm ${taxMessage.type === "success" ? "text-success" : "text-destructive"}`}>
              {taxMessage.text}
            </p>
            <Button variant="ghost" size="sm" className="flex-shrink-0" onClick={() => setTaxMessage(null)} aria-label="Dismiss">
              <X className="h-4 w-4" />
            </Button>
          </div>
        )}
        
        {/* Tax Summary Cards */}
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard
            title="Total Revenue"
            value={formatCurrency(yearlyData.totalRevenue)}
            change={`${yearlyData.totalSales} sales`}
            changeType={yearlyData.totalRevenue >= 0 ? "positive" : "negative"}
            icon={TrendingUp}
            gradient="primary"
          />
          <StatCard
            title="Total Expenses"
            value={formatCurrency(yearlyData.totalExpenses + yearlyData.totalGradingCosts + yearlyData.totalFees)}
            change={`Fees + Grading + Other`}
            changeType={yearlyData.totalExpenses > 0 ? "neutral" : "positive"}
            icon={TrendingDown}
            gradient="secondary"
          />
          <StatCard
            title="Net Profit"
            value={formatCurrency(yearlyData.netProfit)}
            change={`${selectedYear}`}
            changeType={yearlyData.netProfit >= 0 ? "positive" : "negative"}
            icon={DollarSign}
            gradient="tertiary"
          />
          <StatCard
            title="Transactions"
            value={yearlyData.totalTransactions.toString()}
            change={`${yearlyData.totalSales} sold`}
            changeType="neutral"
            icon={Activity}
            gradient="primary"
          />
        </div>
        
        {/* Additional Metrics */}
        <div className="grid gap-6 sm:grid-cols-3">
          <div className="glass-card rounded-xl p-6 animate-fade-in">
            <div className="text-sm text-muted-foreground mb-2">Cost Basis</div>
            <div className="text-2xl font-bold">{formatCurrency(yearlyData.totalCostBasis)}</div>
          </div>
          <div className="glass-card rounded-xl p-6 animate-fade-in">
            <div className="text-sm text-muted-foreground mb-2">Gross Profit</div>
            <div className="text-2xl font-bold">{formatCurrency(yearlyData.grossProfit)}</div>
          </div>
          <div className="glass-card rounded-xl p-6 animate-fade-in">
            <div className="text-sm text-muted-foreground mb-2">Total Fees</div>
            <div className="text-2xl font-bold">{formatCurrency(yearlyData.totalFees)}</div>
          </div>
        </div>
        
        {/* Info Message */}
        {availableYears.length === 0 && (
          <div className="glass-card rounded-xl p-6 text-center">
            <FileText className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
            <h3 className="text-lg font-semibold mb-2">No Tax Data Available</h3>
            <p className="text-muted-foreground">
              Add some transactions and expenses to generate tax reports.
            </p>
          </div>
        )}
      </main>
    </div>
  );
};

export default TaxCenter;
